import { SecurityHardCodePricePayload } from '@repo/types';

export type SecurityListItem = {
  id: number;
  bloombergCode: string;
  currency: string;
  isin: string;
  market: string;
  ricCode: string;
  securityName: string;
  securitySubtype: string;
  securityType: string;
};

export enum SecurityType {
  Cash = 337,
  Equity = 334,
  EquityCFD = 335,
  FixedIncome = 336,
  Fund = 372,
  Futures = 339,
  Index = 496,
  Option = 338,
  StructuredProduct = 1725,
}

export enum SecuritySubType {
  CashWithdrawal = 340,
  CashDeposit = 341,
  ForwardFXContract = 1655,
}

export type SecurityOptionStyle = 'EURO' | 'AMER' | 'ASIA' | 'BERM' | 'OTHR';

export type SecurityParameters = {
  currency: SecurityParameter[];
  exchange: SecurityParameter[];
  market: SecurityParameter[];
  optionStyle: SecurityOptionStyleParameter[];
  optionType: SecurityParameter[];
  securitySubType: Record<SecurityType, SecurityParameter[]>;
  securityType: SecurityParameter[];
  settlementCenter: SecurityParameter[];
  bicsIndustry: Record<number, SecurityParameter[]>;
  bicsIndustryGroup: Record<number, SecurityParameter[]>;
  bicsSector: SecurityParameter[];
  isoSector5: SecurityParameter[];
  msciMarket: SecurityParameter[];
  underlyingId: SecurityParameter[];
  futuresContract: SecurityParameter[];
  marketIssueType: SecurityParameter[];
  fundType: SecurityParameter[];
  securityStatus: SecurityParameter[];
  pricingSource: SecurityParameter[];
};

export type SecurityParameter = {
  id: number;
  value: string;
};

export type SecurityOptionStyleParameter = {
  id: SecurityOptionStyle;
  value: SecurityOptionStyle;
};

export type SecurityPricingCloseField = 'PX_LAST' | 'PX_BID' | 'PX_DISC_MID';

export type SecurityDetail = {
  id: number;
  currency: number;
  fromCcy?: number;
  toCcy?: number;
  exchange: number;
  mifidExchange: number;
  optionStyle?: SecurityOptionStyle | null;
  optionType: number;
  securityType: number;
  securitySubtype: number;
  settlementCenter: number;
  market: number;
  marketIssueType: number;
  isin: string;
  cusip: string;
  sedol: string;
  bloombergCode: string;
  ricCode: string;
  securityName: string;
  identifier1: string;
  identifier2: string;
  identifier3: string;
  identifier4: string;
  identifier5: string;
  futuresContract: number;
  issueDate: string;
  couponFrequency: string;
  couponRate: string;
  strikePrice: string;
  expiryDate: string;
  underlyingId: number;
  multiplier: string;
  securityStatus: number;
  countryOfIssue: string;
  listedProduct: boolean;
  fundType?: string;
  oecd: boolean;
  issuer: string;
  bicsSector: number | null;
  bicsIndustryGroup: number | null;
  bicsIndustry: number | null;
  mifidTransactionFlag: boolean;
  mifidCfiCode: string;
  createdBy: string;
  updatedBy: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  msciMarket: number;
  isoSector5: number;
  frequency?: SecurityFrequency;
  observationDate?: string;
  nextCouponDate?: string;
  pricingCloseField?: SecurityPricingCloseField | null;
  pricingSource?: string | null;
};

export type SecurityDictionaryPrefillIdentifierType = 'bloombergCode' | 'isin';

export type SecurityDictionaryPrefillParams = {
  identifierType: SecurityDictionaryPrefillIdentifierType;
  identifierValue: string;
  securityType: SecurityType;
};

export type SecurityUnderlyingResolutionStatus =
  | 'existing'
  | 'missing'
  | 'ambiguous'
  | 'notReturned';

export type SecurityUnderlyingPreview = {
  [K in keyof Omit<SecurityDetail, 'securityType'>]?: SecurityDetail[K] | null;
} & {
  securityType?: SecurityType | null;
};

export type SecurityDictionaryPrefillResponse = {
  [K in keyof Omit<SecurityDetail, 'securityType'>]?: SecurityDetail[K] | null;
} & {
  securityType?: SecurityType | null;
  underlyingInstrumentIsin?: string | null;
  underlyingPreview?: SecurityUnderlyingPreview | null;
  underlyingResolutionStatus?: SecurityUnderlyingResolutionStatus | null;
};

export enum SecurityFrequency {
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  SemiAnnually = 'Semi-Annually',
  Annually = 'Annually',
}

export type SecurityListShortItem = {
  bloombergCode: string;
  id: number;
  isin: string;
  securityName: string;
  multiplier: string;
  currency: number;
};

export type SecurityDetailWithHardCodePrice = SecurityDetail & {
  hardCodePrice: SecurityHardCodePricePayload[];
};
